import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { formatInvoiceAmount } from "@/components/incoming-invoices";
import { allocationCents } from "./allocation-sum";
import { type CostAllocationFormValues, shareOf } from "./cost-allocation-form-values";
import { CostAllocationFormRow } from "./cost-allocation-form-row";

/** Props Interface */
interface IncomingInvoiceCostAllocationFormProps {
	netAmount: number;
	currency: string;
}

/**
 * An invoice's cost allocation as rows of a cost centre and an amount, with what is still to be
 * allocated beneath them. Each row shows its share of the net amount and can take the rest.
 */
export function IncomingInvoiceCostAllocationForm({
	netAmount,
	currency,
}: IncomingInvoiceCostAllocationFormProps) {
	/** Context */
	const methods = useFormContext<CostAllocationFormValues>();

	/** Hooks */
	const { t } = useTranslation();
	const { fields, append, remove } = useFieldArray({
		control: methods.control,
		name: "allocations",
	});
	const allocations = useWatch({ control: methods.control, name: "allocations" });

	/** Computed */
	const remainingCents =
		Math.round(netAmount * 100) -
		allocationCents((allocations ?? []).map((allocation) => allocation.amount));
	const rootError = methods.formState.errors.root?.message;

	/** Functions */
	function handleOnAddRow() {
		const amount = Math.max(remainingCents, 0) / 100;
		append({ costCenterId: "", amount, share: shareOf(amount, netAmount) });
	}

	/** Render */
	return (
		<div className="v-stack gap-4">
			{fields.map((field, index) => (
				<CostAllocationFormRow
					key={field.id}
					index={index}
					netAmount={netAmount}
					remainingCents={remainingCents}
					onRemove={() => remove(index)}
				/>
			))}
			<div className="h-stack items-center justify-between">
				<Button
					variant="white"
					size="sm"
					icon={<Icon name="Plus" />}
					onClick={handleOnAddRow}
					data-cy="add-cost-allocation-row-button"
				>
					{t("incoming_invoices.add_cost_allocation_row")}
				</Button>
				<p
					className={remainingCents === 0 ? "label" : "text-rose-500 tabular-nums"}
					data-cy="cost-allocation-remaining"
				>
					{t("incoming_invoices.cost_allocation_remaining", {
						amount: formatInvoiceAmount(remainingCents / 100, currency),
					})}
				</p>
			</div>
			{rootError && <p className="text-rose-500 text-sm">{rootError}</p>}
		</div>
	);
}
